import React, { useState } from "react";

import FormSection from "../../FundForm/FormSection";
import { Download, Eye, FileText } from "../../FundForm/icons";
import {
  getMimeType,
  splitAtLastOccurrenceOfItem,
} from "../../../../../NewComponents/CustomViewDocumentHandler/helper";
import { ADVANCLY_STORAGE_URL, TSavedFund } from "../previewFund";
import TermSheetPreviewModal from "./TermSheetPreviewModal";
import styles from "../previewFund.module.scss";

interface IVisibilityDocumentsCard {
  fund: TSavedFund;
  isVisible: boolean;
  step: string;
}

/**
 * Who can see the fund, and its term sheet — read in place or downloaded.
 *
 * The preview opens the file from storage directly, the same URL the download
 * uses, so nothing has to be fetched twice.
 */
export default function VisibilityDocumentsCard({
  fund,
  isVisible,
  step,
}: IVisibilityDocumentsCard) {
  const [isPreviewing, setIsPreviewing] = useState(false);

  const { term_sheet_url: path, term_sheet_filename: fileName } = fund;
  const url = path ? `${ADVANCLY_STORAGE_URL}${path}` : "";
  const mimeType = path
    ? getMimeType(splitAtLastOccurrenceOfItem(path.toLowerCase(), "."))
    : "";

  const download = () => window.open(url, "_blank", "noopener,noreferrer");

  return (
    <FormSection
      id="visibility"
      title="Visibility & documents"
      subtitle="Who can see the fund, and the term sheet that goes with it."
      icon={<Eye color="#713fff" />}
      meta={step}
    >
      <div className={styles.stack}>
        <div className={`${styles.row} ${styles.rowTinted}`}>
          <div>
            <div className={styles.rowTitle}>
              {isVisible ? "Visible to customers" : "Hidden from customers"}
            </div>
            <div className={styles.rowCopy}>
              {isVisible
                ? "Customers can find and subscribe to this fund on Digital Bank."
                : "Customers cannot see this fund on Digital Bank."}
            </div>
          </div>
          <span
            className={`${styles.chipOk} ${isVisible ? "" : styles.chipMuted}`}
            style={{ marginTop: 0 }}
          >
            {isVisible ? "On" : "Off"}
          </span>
        </div>

        <div className={styles.row}>
          <div className={styles.file}>
            <div className={styles.fileIcon}>
              <FileText size={18} color="#713fff" />
            </div>
            <div style={{ minWidth: 0 }}>
              <div className={styles.rowCopy} style={{ marginTop: 0 }}>
                Term sheet
              </div>
              <div
                className={`${styles.fileName} ${
                  fileName ? "" : styles.notSet
                }`}
                title={fileName ?? undefined}
              >
                {fileName || "None attached"}
              </div>
            </div>
          </div>

          {path ? (
            <div className={styles.buttons}>
              <button
                type="button"
                className={styles.button}
                disabled={!mimeType}
                title={
                  mimeType ? undefined : "This file type cannot be previewed"
                }
                onClick={() => setIsPreviewing(true)}
              >
                <Eye size={16} />
                Preview
              </button>
              <button
                type="button"
                className={styles.buttonOutline}
                onClick={download}
              >
                <Download />
                Download
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {path && mimeType ? (
        <TermSheetPreviewModal
          open={isPreviewing}
          url={url}
          fileName={fileName || path}
          mimeType={mimeType}
          onDownload={download}
          onClose={() => setIsPreviewing(false)}
        />
      ) : null}
    </FormSection>
  );
}
